import React, { useEffect, useState, memo, useCallback } from "react";
import axios from "axios";
import { getToken } from "../../utils/auth";
import { useTranslation } from "react-i18next";
import CONFIG from "../../config";

const CategoryAdmin = memo(() => {
    const { t, i18n } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", name_ar: "", is_featured: 0, icon: "", image_url: "" });
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState("");


    const isArabic = i18n.language?.startsWith('ar');
    const [permissions, setPermissions] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        setIsAdmin(!!user.isAdmin);
        setPermissions(user.permissions || []);
    }, []);

    const hasPerm = (p) => {
        if (!isAdmin) return false;
        if (permissions.includes('*') || permissions === '*') return true;
        return permissions.includes(p);
    };

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${CONFIG.API_URL}/api/categories`);
            setCategories(res.data || []);
        } catch (err) {
            console.error(err);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);



    const getHeaders = () => {
        const token = getToken();
        if (token) return { Authorization: `Bearer ${token}` };
        return {};
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const data = new FormData();
        Object.keys(form).forEach(key => data.append(key, form[key]));
        if (imageFile) data.append("image", imageFile);

        try {
            if (editing) {
                await axios.put(`${CONFIG.API_URL}/api/categories/${editing}`, data, { headers: getHeaders() });
            } else {
                await axios.post(`${CONFIG.API_URL}/api/categories`, data, { headers: getHeaders() });
            }
            setEditing(null);
            setForm({ name: "", name_ar: "", is_featured: 0, icon: "", image_url: "" });
            setImageFile(null);
            setPreview("");
            fetchCategories();
        } catch (err) {
            alert(err.response?.data?.message || t("admin.common.operation_failed"));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t("admin.category_admin.delete_confirm"))) return;
        try {
            await axios.delete(`${CONFIG.API_URL}/api/categories/${id}`, { headers: getHeaders() });
            fetchCategories();
        } catch (err) {
            alert(t("admin.category_admin.delete_failed"));
        }
    };

    const toggleFeatured = async (id) => {
        try {
            await axios.post(`${CONFIG.API_URL}/api/categories/${id}/toggle-featured`, {}, { headers: getHeaders() });
            fetchCategories();
        } catch (err) {
            alert(t("admin.category_admin.toggle_failed"));
        }
    };

    const startEdit = (c) => {
        setEditing(c.id);
        setForm({
            name: c.name,
            name_ar: c.name_ar,
            is_featured: c.is_featured,
            icon: c.icon || "",
            image_url: c.image_url || ""
        });
        setPreview(c.image_url || "");
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreview(URL.createObjectURL(file));
        }
    };


    const [search, setSearch] = useState("");

    const filteredCategories = categories.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.name_ar.toLowerCase().includes(search.toLowerCase()) ||
        c.id.toString().includes(search)
    );

    return (
        <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-xl shadow-lg border border-gray-100 mt-24">
            <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800">{t("admin.category_admin.title")}</h2>

                <button
                    onClick={() => window.history.back()}
                    className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-colors text-sm font-bold group"
                >
                    <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                    {t('admin.common.back', 'Back')}
                </button>
            </div>

            <form onSubmit={handleSubmit} className="mb-12 bg-gray-50 p-6 rounded-xl border border-gray-100">
                <h3 className="font-bold mb-4">{editing ? t("admin.category_admin.edit") : t("admin.category_admin.create")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        placeholder={t("admin.category_admin.name_en")}
                        className="border p-3 rounded-lg focus:ring-2 focus:ring-brand-primary/20 outline-none font-medium"
                        required
                    />
                    <input
                        value={form.name_ar}
                        onChange={e => setForm({ ...form, name_ar: e.target.value })}
                        placeholder={t("admin.category_admin.name_ar")}
                        className="border p-3 rounded-lg focus:ring-2 focus:ring-brand-primary/20 outline-none text-right font-medium"
                        required
                    />
                    <input
                        value={form.icon}
                        onChange={e => setForm({ ...form, icon: e.target.value })}
                        placeholder={t("admin.category_admin.icon_placeholder")}
                        className="border p-3 rounded-lg focus:ring-2 focus:ring-brand-primary/20 outline-none font-medium"
                    />
                    <div className="flex flex-col gap-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">{t('admin.products_page.form.category_image', 'Category Image')}</label>
                        <div
                            className="border-2 border-dashed border-gray-200 rounded-2xl bg-white p-6 text-center cursor-pointer hover:border-brand-primary transition-all group"
                            onClick={() => document.getElementById('category-image-input').click()}
                        >
                            <input
                                id="category-image-input"
                                type="file"
                                onChange={handleFileChange}
                                accept="image/*"
                                className="hidden"
                            />
                            <div className="flex flex-col items-center gap-2">
                                <i className="fas fa-image text-2xl text-gray-300 group-hover:text-brand-primary transition-colors"></i>
                                <span className="text-xs font-black uppercase tracking-widest text-gray-900">
                                    {imageFile ? imageFile.name : t('admin.products_page.form.click_to_browse', 'Click to browse')}
                                </span>
                                {!imageFile && <span className="text-[10px] text-gray-400 font-medium">{t('admin.category_admin.no_file_selected', 'No file selected.')}</span>}
                            </div>
                        </div>
                        {preview && (
                            <div className="relative h-24 w-full rounded-2xl overflow-hidden border border-gray-100 shadow-sm mt-2">
                                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                                <button type="button" onClick={() => { setImageFile(null); setPreview(""); setForm({ ...form, image_url: "" }); }} className="absolute top-2 right-2 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs shadow-lg">
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="isFeatured"
                        checked={form.is_featured === 1}
                        onChange={e => setForm({ ...form, is_featured: e.target.checked ? 1 : 0 })}
                    />
                    <label htmlFor="isFeatured" className="text-sm font-medium text-gray-700">{t("admin.category_admin.show_home")}</label>
                </div>
                <div className="mt-6 flex gap-2">
                    <button type="submit" disabled={saving} className="bg-brand-primary text-white px-6 py-2 rounded-lg font-bold flex items-center gap-2">
                        {saving && <i className="fas fa-spinner fa-spin"></i>}
                        {saving ? t("admin.common.saving", "Saving...") : (editing ? t("admin.category_admin.update_btn") : t("admin.category_admin.create_btn"))}
                    </button>
                    {editing && <button type="button" onClick={() => { setEditing(null); setForm({ name: "", name_ar: "", is_featured: 0, icon: "", image_url: "" }); setPreview(""); setImageFile(null); }} className="text-gray-500">{t("admin.common.cancel")}</button>}
                </div>
            </form>

            <div className="mb-8 flex items-center bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div className="relative w-full">
                    <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('admin.common.search_placeholder', 'Search by name or ID...')}
                        className="w-full pl-12 pr-4 py-3 rounded-lg border-none bg-white shadow-sm focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium"
                    />
                </div>
            </div>

            {
                loading ? <p>{t("admin.common.loading")}</p> : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b">
                                    <th className="py-3 px-4">{t("admin.common.id", "ID")}</th>
                                    <th className="py-3 px-4">{t("admin.category_admin.preview")}</th>
                                    <th className="py-3 px-4">{t("admin.category_admin.table_name_en")}</th>
                                    <th className="py-3 px-4">{t("admin.category_admin.table_name_ar")}</th>
                                    {hasPerm('categories.icon') && <th className="py-3 px-4">{t("admin.category_admin.table_icon")}</th>}
                                    {hasPerm('categories.is_featured') && <th className="py-3 px-4">{t("admin.category_admin.table_home")}</th>}
                                    <th className="py-3 px-4">{t("admin.category_admin.actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCategories.map(c => (
                                    <tr key={c.id} className="border-b hover:bg-gray-50">
                                        <td className="py-3 px-4 font-mono text-gray-500 text-sm">#{c.id}</td>
                                        <td className="py-3 px-4">
                                            {c.image_url ? <img src={c.image_url} alt="" className="w-10 h-10 object-cover rounded-lg shadow-sm" /> : <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-[10px]">{t("admin.hero_admin.placeholders.no_file_selected", "No Img")}</div>}
                                        </td>
                                        <td className="py-3 px-4 font-bold text-gray-900">{c.name}</td>
                                        <td className="py-3 px-4 text-right font-medium text-gray-600">{c.name_ar}</td>
                                        {hasPerm('categories.icon') && (
                                            <td className="py-3 px-4 text-center">
                                                <i className={`fas ${c.icon || 'fa-question'} text-brand-primary`}></i>
                                            </td>
                                        )}
                                        {hasPerm('categories.is_featured') && (
                                            <td className="py-3 px-4">
                                                <button
                                                    onClick={() => toggleFeatured(c.id)}
                                                    className={`px-3 py-1 rounded-full text-[10px] font-black uppercase transition-all ${c.is_featured ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                                                >
                                                    {c.is_featured ? t("admin.category_admin.featured") : t("admin.category_admin.hidden")}
                                                </button>
                                            </td>
                                        )}
                                        <td className="py-3 px-4 space-x-2">
                                            <button onClick={() => startEdit(c)} className="text-blue-600 hover:underline">{t("admin.common.edit")}</button>
                                            <button onClick={() => handleDelete(c.id)} className="text-red-500 hover:underline">{t("admin.common.delete")}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }
        </div >
    );
});

export default CategoryAdmin;
